import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
    ArrowLeft,
    Package,
    Plus,
    Edit,
    X,
    Loader2,
    CheckCircle2,
    Camera,
    UserCheck,
    AlertCircle,
    ChevronDown,
    ChevronUp
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/ui/toaster';
import api from '../../services/api';

const EntityChecklistMaster = () => {
    const navigate = useNavigate();

    // State
    const [entities, setEntities] = useState([]);
    const [selectedEntity, setSelectedEntity] = useState(null);
    const [loading, setLoading] = useState(true);

    // Modals state
    const [showEntityModal, setShowEntityModal] = useState(false);
    const [showChecklistModal, setShowChecklistModal] = useState(false);
    const [editingEntity, setEditingEntity] = useState(null);
    const [editingChecklist, setEditingChecklist] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Collapsible groups state
    const [collapsedGroups, setCollapsedGroups] = useState(new Set());

    // Form data
    const [entityForm, setEntityForm] = useState({
        entityName: '',
        description: '',
        displayOrder: 0,
        isActive: true
    });

    const [checklistForm, setChecklistForm] = useState({
        itemName: '',
        description: '',
        groupName: 'General',
        isRequired: true,
        requiresPhoto: false,
        requiresSupervisorApproval: false,
        isActive: true
    });

    useEffect(() => {
        fetchAllEntitiesWithChecklists();
    }, []);

    const fetchAllEntitiesWithChecklists = async () => {
        setLoading(true);
        try {
            const response = await api.getAllEntitiesWithChecklists();
            const entitiesData = response.entities || [];

            setEntities(entitiesData);

            // If an entity is already selected, refresh it with updated data
            if (selectedEntity) {
                const updatedSelectedEntity = entitiesData.find(e => e.id === selectedEntity.id);
                if (updatedSelectedEntity) {
                    setSelectedEntity(updatedSelectedEntity);
                }
            } else if (entitiesData.length > 0) {
                // No entity selected, select the first one
                setSelectedEntity(entitiesData[0]);
            }
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to fetch entities and checklists',
                variant: 'destructive'
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchEntities = async () => {
        setLoading(true);
        try {
            const response = await api.getEntities(1, 100, '');
            setEntities(response.items || []);
            if (response.items && response.items.length > 0 && !selectedEntity) {
                selectEntity(response.items[0]);
            }
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to fetch entities',
                variant: 'destructive'
            });
        } finally {
            setLoading(false);
        }
    };

    const selectEntity = (entity) => {
        // Find the full entity data from our local state
        const fullEntity = entities.find(e => e.id === entity.id);
        if (fullEntity) {
            setSelectedEntity(fullEntity);
            // Reset collapsed groups when selecting a new entity
            setCollapsedGroups(new Set());
        }
    };

    const toggleGroupCollapse = (groupName) => {
        setCollapsedGroups(prev => {
            const newSet = new Set(prev);
            if (newSet.has(groupName)) {
                newSet.delete(groupName);
            } else {
                newSet.add(groupName);
            }
            return newSet;
        });
    };

    const openEntityModal = (entity = null) => {
        if (entity) {
            setEditingEntity(entity);
            setEntityForm({
                entityName: entity.entityName,
                description: entity.description || '',
                displayOrder: entity.displayOrder,
                isActive: entity.isActive
            });
        } else {
            setEditingEntity(null);
            setEntityForm({
                entityName: '',
                description: '',
                displayOrder: 0,
                isActive: true
            });
        }
        setShowEntityModal(true);
    };

    const openChecklistModal = (checklistItem = null) => {
        if (!selectedEntity) {
            toast({
                title: 'No Entity Selected',
                description: 'Please select an entity first',
                variant: 'destructive'
            });
            return;
        }

        if (checklistItem) {
            setEditingChecklist(checklistItem);
            setChecklistForm({
                itemName: checklistItem.itemName,
                description: checklistItem.description,
                groupName: checklistItem.groupName || '',
                isRequired: checklistItem.isRequired,
                requiresPhoto: checklistItem.requiresPhoto,
                requiresSupervisorApproval: checklistItem.requiresSupervisorApproval,
                isActive: checklistItem.isActive
            });
        } else {
            setEditingChecklist(null);
            setChecklistForm({
                itemName: '',
                description: '',
                groupName: 'General',
                isRequired: true,
                requiresPhoto: false,
                requiresSupervisorApproval: false,
                isActive: true
            });
        }
        setShowChecklistModal(true);
    };

    const handleSaveEntity = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            if (editingEntity) {
                await api.updateEntity(editingEntity.id, entityForm);
                toast({
                    title: 'Success',
                    description: 'Entity updated successfully'
                });
            } else {
                const newEntity = await api.createEntity(entityForm);
                toast({
                    title: 'Success',
                    description: 'Entity created successfully'
                });
                setSelectedEntity(newEntity);
            }

            setShowEntityModal(false);
            setEditingEntity(null);
            setEntityForm({
                entityName: '',
                description: '',
                displayOrder: 0,
                isActive: true
            });
            fetchAllEntitiesWithChecklists(); // Reload all data
        } catch (error) {
            toast({
                title: 'Error',
                description: error.message || 'Failed to save entity',
                variant: 'destructive'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleSaveChecklist = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            // Validate: Group name is required
            if (!checklistForm.groupName || !checklistForm.groupName.trim()) {
                toast({
                    title: 'Validation Error',
                    description: 'Group name is required for all checklist items',
                    variant: 'destructive'
                });
                setSubmitting(false);
                return;
            }

            const payload = {
                ...checklistForm,
                entityId: selectedEntity.id
            };

            if (editingChecklist) {
                await api.updateMstChecklistItem(editingChecklist.id, checklistForm);
                toast({
                    title: 'Success',
                    description: 'Checklist item updated successfully'
                });
            } else {
                await api.createChecklistItem(payload);
                toast({
                    title: 'Success',
                    description: 'Checklist item created successfully'
                });
            }

            setShowChecklistModal(false);
            setEditingChecklist(null);
            setChecklistForm({
                itemName: '',
                description: '',
                groupName: 'General',
                isRequired: true,
                requiresPhoto: false,
                requiresSupervisorApproval: false,
                isActive: true
            });
            fetchAllEntitiesWithChecklists(); // Reload all data
        } catch (error) {
            toast({
                title: 'Error',
                description: error.message || 'Failed to save checklist item',
                variant: 'destructive'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteEntity = async (entityId) => {
        if (!window.confirm('Are you sure you want to delete this entity? This will also delete all its checklist items.')) {
            return;
        }

        try {
            await api.deleteEntity(entityId);
            toast({
                title: 'Success',
                description: 'Entity deleted successfully'
            });
            setSelectedEntity(null);
            fetchAllEntitiesWithChecklists();
        } catch (error) {
            toast({
                title: 'Error',
                description: error.message || 'Failed to delete entity',
                variant: 'destructive'
            });
        }
    };

    const handleDeleteChecklist = async (checklistId) => {
        if (!window.confirm('Are you sure you want to delete this checklist item?')) {
            return;
        }

        try {
            await api.deleteChecklistItem(checklistId);
            toast({
                title: 'Success',
                description: 'Checklist item deleted successfully'
            });
            fetchAllEntitiesWithChecklists();
        } catch (error) {
            toast({
                title: 'Error',
                description: error.message || 'Failed to delete checklist item',
                variant: 'destructive'
            });
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <Toaster />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center space-x-3">
                            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/settings')}>
                                <ArrowLeft className="h-5 w-5" />
                            </Button>
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shrink-0">
                                <Package className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Entity & Checklist Master</h1>
                                <p className="text-gray-600 text-sm sm:text-base">Manage installation entities and their checklist items</p>
                            </div>
                        </div>
                        <Button onClick={() => navigate('/admin/settings/entity-checklist/create')} className="bg-teal-600 hover:bg-teal-700 self-start sm:self-auto">
                            <Plus className="h-4 w-4 mr-2" /> New Entity
                        </Button>
                    </div>
                </div>

                {/* Master-Detail Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                    {/* LEFT PANEL - Entity List */}
                    <Card className="lg:col-span-1">
                        <CardHeader>
                            <CardTitle className="text-lg">Entities ({entities.length})</CardTitle>
                        </CardHeader>
                        <CardContent className="p-2">
                            <div className="space-y-2 max-h-[600px] overflow-y-auto">
                                {entities.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        <Package className="h-12 w-12 mx-auto mb-2 opacity-30" />
                                        <p>No entities found</p>
                                        <Button
                                            variant="link"
                                            onClick={() => openEntityModal()}
                                            className="text-teal-600"
                                        >
                                            Create your first entity
                                        </Button>
                                    </div>
                                ) : (
                                    entities.map((entity) => (
                                        <div
                                            key={entity.id}
                                            onClick={() => selectEntity(entity)}
                                            className={`p-3 rounded-lg cursor-pointer transition-all ${selectedEntity?.id === entity.id
                                                ? 'bg-teal-50 border-2 border-teal-500'
                                                : 'bg-white border border-gray-200 hover:border-teal-300'
                                                }`}
                                        >
                                            <h3 className="font-semibold text-gray-900">{entity.entityName}</h3>
                                            {entity.description && (
                                                <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                                    {entity.description}
                                                </p>
                                            )}
                                            <div className="flex items-center gap-3 mt-2">
                                                <span className="text-xs text-gray-500">
                                                    {entity.checklistItems?.length || 0} items
                                                </span>
                                                {entity.isActive ? (
                                                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                                        Active
                                                    </span>
                                                ) : (
                                                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                                                        Inactive
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* RIGHT PANEL - Entity Details & Checklist */}
                    <Card className="lg:col-span-2">
                        {selectedEntity ? (
                            <>
                                <CardHeader>
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <CardTitle className="text-2xl">{selectedEntity.entityName}</CardTitle>
                                            {selectedEntity.description && (
                                                <p className="text-gray-600 mt-2">{selectedEntity.description}</p>
                                            )}
                                            <div className="flex items-center gap-4 mt-3">
                                                <span className="text-sm text-gray-500">
                                                    Display Order: {selectedEntity.displayOrder}
                                                </span>
                                                {selectedEntity.isActive ? (
                                                    <span className="text-sm bg-green-100 text-green-700 px-3 py-1 rounded-full">
                                                        Active
                                                    </span>
                                                ) : (
                                                    <span className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded-full">
                                                        Inactive
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => openEntityModal(selectedEntity)}
                                            >
                                                <Edit className="h-4 w-4 mr-1" /> Edit
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleDeleteEntity(selectedEntity.id)}
                                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                            >
                                                <X className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div className="border-t pt-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-lg font-semibold text-gray-900">
                                                Checklist Items ({selectedEntity.checklistItems?.length || 0})
                                            </h3>
                                            <Button
                                                onClick={() => openChecklistModal()}
                                                size="sm"
                                                className="bg-teal-600 hover:bg-teal-700"
                                            >
                                                <Plus className="h-4 w-4 mr-1" /> Add Item
                                            </Button>
                                        </div>

                                        {!selectedEntity.checklistItems || selectedEntity.checklistItems.length === 0 ? (
                                            <div className="text-center py-12 bg-gray-50 rounded-lg">
                                                <AlertCircle className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                                                <p className="text-gray-600 mb-4">No checklist items yet</p>
                                                <Button
                                                    onClick={() => openChecklistModal()}
                                                    variant="outline"
                                                    className="border-teal-600 text-teal-600 hover:bg-teal-50"
                                                >
                                                    <Plus className="h-4 w-4 mr-2" /> Add First Item
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="space-y-6">
                                                {(() => {
                                                    // Group items by group name
                                                    const groupedItems = selectedEntity.checklistItems.reduce((acc, item) => {
                                                        const groupKey = item.groupName || 'General';
                                                        if (!acc[groupKey]) {
                                                            acc[groupKey] = {
                                                                groupName: groupKey,
                                                                groupDisplayOrder: item.groupDisplayOrder ?? 999,
                                                                items: []
                                                            };
                                                        }
                                                        acc[groupKey].items.push(item);
                                                        return acc;
                                                    }, {});

                                                    // Sort groups by groupDisplayOrder, then items within groups
                                                    const sortedGroups = Object.values(groupedItems)
                                                        .sort((a, b) => a.groupDisplayOrder - b.groupDisplayOrder);

                                                    return sortedGroups.map((group, groupIndex) => {
                                                        const sortedItems = group.items.sort((a, b) =>
                                                            a.displayOrder - b.displayOrder || a.id.localeCompare(b.id)
                                                        );

                                                        const isCollapsed = collapsedGroups.has(group.groupName);

                                                        return (
                                                            <div key={groupIndex} className="space-y-3">
                                                                {/* Group Header */}
                                                                <div
                                                                    className="flex items-center gap-3 mb-3 cursor-pointer hover:opacity-75 transition-opacity"
                                                                    onClick={() => toggleGroupCollapse(group.groupName)}
                                                                >
                                                                    <div className="h-px bg-gray-300 flex-1"></div>
                                                                    <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded hover:bg-gray-200 transition-colors">
                                                                        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                                                                            {group.groupName}
                                                                        </h4>
                                                                        <span className="text-xs text-gray-500">({sortedItems.length})</span>
                                                                        {isCollapsed ? (
                                                                            <ChevronDown className="h-4 w-4 text-gray-600" />
                                                                        ) : (
                                                                            <ChevronUp className="h-4 w-4 text-gray-600" />
                                                                        )}
                                                                    </div>
                                                                    <div className="h-px bg-gray-300 flex-1"></div>
                                                                </div>

                                                                {/* Group Items */}
                                                                {!isCollapsed && sortedItems.map((item, itemIndex) => (
                                                                    <div
                                                                        key={item.id}
                                                                        className="p-4 bg-white border border-gray-200 rounded-lg hover:border-teal-300 transition-all"
                                                                    >
                                                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
                                                                            <div className="flex items-start sm:items-center gap-2 flex-1 min-w-0">
                                                                                <span className="w-8 h-8 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center text-sm font-semibold shrink-0">
                                                                                    {itemIndex + 1}
                                                                                </span>
                                                                                <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:flex-wrap gap-2">
                                                                                    <h4 className="font-semibold text-gray-900 break-words sm:truncate">{item.itemName}</h4>
                                                                                    <div className="flex flex-wrap items-center gap-2">
                                                                                        {item.isRequired && (
                                                                                            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded shrink-0">
                                                                                                Required
                                                                                            </span>
                                                                                        )}
                                                                                        {item.requiresPhoto && (
                                                                                            <span className="text-xs text-blue-600 flex items-center gap-1 shrink-0">
                                                                                                <Camera className="h-3 w-3" /> Photo
                                                                                            </span>
                                                                                        )}
                                                                                        {item.requiresSupervisorApproval && (
                                                                                            <span className="text-xs text-purple-600 flex items-center gap-1 shrink-0">
                                                                                                <UserCheck className="h-3 w-3" /> Supervisor
                                                                                            </span>
                                                                                        )}
                                                                                        {!item.isActive && (
                                                                                            <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded shrink-0">
                                                                                                Inactive
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                            <div className="flex gap-2 shrink-0 self-end sm:self-auto">
                                                                                <Button
                                                                                    variant="ghost"
                                                                                    size="icon"
                                                                                    onClick={() => openChecklistModal(item)}
                                                                                    className="h-8 w-8"
                                                                                >
                                                                                    <Edit className="h-4 w-4" />
                                                                                </Button>
                                                                                <Button
                                                                                    variant="ghost"
                                                                                    size="icon"
                                                                                    onClick={() => handleDeleteChecklist(item.id)}
                                                                                    className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                                >
                                                                                    <X className="h-4 w-4" />
                                                                                </Button>
                                                                            </div>
                                                                        </div>
                                                                        {item.description && (
                                                                            <p className="text-sm text-gray-600 ml-10 mt-2">{item.description}</p>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        );
                                                    });
                                                })()}
                                            </div>
                                        )}
                                    </div>
                                </CardContent>
                            </>
                        ) : (
                            <CardContent className="flex items-center justify-center min-h-[600px]">
                                <div className="text-center">
                                    <Package className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                                    <h3 className="text-lg font-semibold text-gray-600 mb-2">No Entity Selected</h3>
                                    <p className="text-gray-500">Select an entity from the list to view details</p>
                                </div>
                            </CardContent>
                        )}
                    </Card>
                </div>
            </div>

            {/* Entity Modal */}
            {showEntityModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold">
                                {editingEntity ? 'Edit Entity' : 'Create New Entity'}
                            </h2>
                            <Button variant="ghost" size="icon" onClick={() => setShowEntityModal(false)}>
                                <X className="h-5 w-5" />
                            </Button>
                        </div>

                        <form onSubmit={handleSaveEntity}>
                            <div className="space-y-4">
                                <div>
                                    <Label htmlFor="entityName">Entity Name *</Label>
                                    <Input
                                        id="entityName"
                                        value={entityForm.entityName}
                                        onChange={(e) => setEntityForm({ ...entityForm, entityName: e.target.value })}
                                        placeholder="e.g., Kitchen Wall Unit"
                                        required
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="description">Description</Label>
                                    <Textarea
                                        id="description"
                                        value={entityForm.description}
                                        onChange={(e) => setEntityForm({ ...entityForm, description: e.target.value })}
                                        placeholder="Brief description of the entity"
                                        rows={3}
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="displayOrder">Display Order</Label>
                                    <Input
                                        id="displayOrder"
                                        type="number"
                                        min="0"
                                        value={entityForm.displayOrder}
                                        onChange={(e) => setEntityForm({ ...entityForm, displayOrder: Math.max(0, parseInt(e.target.value) || 0) })}
                                    />
                                </div>

                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="isActive"
                                        checked={entityForm.isActive}
                                        onChange={(e) => setEntityForm({ ...entityForm, isActive: e.target.checked })}
                                        className="rounded"
                                    />
                                    <Label htmlFor="isActive">Active</Label>
                                </div>
                            </div>

                            <div className="flex gap-3 mt-6">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setShowEntityModal(false)}
                                    className="flex-1"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={submitting}
                                    className="flex-1 bg-teal-600 hover:bg-teal-700"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle2 className="mr-2 h-4 w-4" />
                                            Save
                                        </>
                                    )}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Checklist Item Modal */}
            {showChecklistModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[85dvh] sm:max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold">
                                {editingChecklist ? 'Edit Checklist Item' : 'Add Checklist Item'}
                            </h2>
                            <Button variant="ghost" size="icon" onClick={() => setShowChecklistModal(false)}>
                                <X className="h-5 w-5" />
                            </Button>
                        </div>

                        <form onSubmit={handleSaveChecklist}>
                            <div className="space-y-4">
                                <div>
                                    <Label htmlFor="itemName">Item Name *</Label>
                                    <Input
                                        id="itemName"
                                        value={checklistForm.itemName}
                                        onChange={(e) => setChecklistForm({ ...checklistForm, itemName: e.target.value })}
                                        placeholder="e.g., Check wall alignment"
                                        required
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="checklistDescription">Description</Label>
                                    <Textarea
                                        id="checklistDescription"
                                        value={checklistForm.description}
                                        onChange={(e) => setChecklistForm({ ...checklistForm, description: e.target.value })}
                                        placeholder="Detailed instructions for this checklist item"
                                        rows={3}
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="groupName">
                                        Group Name <span className="text-red-500">*</span>
                                    </Label>
                                    <Input
                                        id="groupName"
                                        value={checklistForm.groupName || ''}
                                        onChange={(e) => setChecklistForm({ ...checklistForm, groupName: e.target.value })}
                                        placeholder="e.g., Material Checklist, Installation, General"
                                        required
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Items are displayed grouped by name</p>
                                </div>

                                <div className="space-y-2 pt-2 border-t">
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            id="isRequired"
                                            checked={checklistForm.isRequired}
                                            onChange={(e) => setChecklistForm({ ...checklistForm, isRequired: e.target.checked })}
                                            className="rounded"
                                        />
                                        <Label htmlFor="isRequired">Required (mandatory item)</Label>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            id="requiresPhoto"
                                            checked={checklistForm.requiresPhoto}
                                            onChange={(e) => setChecklistForm({ ...checklistForm, requiresPhoto: e.target.checked })}
                                            className="rounded"
                                        />
                                        <Label htmlFor="requiresPhoto">Requires Photo</Label>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            id="requiresSupervisorApproval"
                                            checked={checklistForm.requiresSupervisorApproval}
                                            onChange={(e) => setChecklistForm({ ...checklistForm, requiresSupervisorApproval: e.target.checked })}
                                            className="rounded"
                                        />
                                        <Label htmlFor="requiresSupervisorApproval">Requires Supervisor Approval</Label>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            id="checklistIsActive"
                                            checked={checklistForm.isActive}
                                            onChange={(e) => setChecklistForm({ ...checklistForm, isActive: e.target.checked })}
                                            className="rounded"
                                        />
                                        <Label htmlFor="checklistIsActive">Active</Label>
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-3 mt-6">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setShowChecklistModal(false)}
                                    className="flex-1"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={submitting}
                                    className="flex-1 bg-teal-600 hover:bg-teal-700"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle2 className="mr-2 h-4 w-4" />
                                            Save
                                        </>
                                    )}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EntityChecklistMaster;
